import NotificationBell from '../../notifications/notification-bell';
import React, { useState } from 'react';
import { Dropdown, Nav, Navbar } from 'react-bootstrap';
import { List, X } from 'react-bootstrap-icons';
import './top-bar.scss';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { useKeycloak } from '@react-keycloak/web';
import { getAppName, isAzureIDP, isKeycloakIDP } from '../../../utils/loginProvider';
import { useGetUserDataQuery } from '../../../store/user/user.api';
import LanguageSwitcher from '../../languageSwitcher/languageSwitcher';
import { clearAccessTokenLocalStorage } from '../../../utils/token';
import { ReactComponent as AccountitzLogo } from '../../../assets/accountitz-logo-inverse.svg';
import { ReactComponent as Avatar } from '../../../assets/avatar.svg';
import { ReactComponent as ChevronDown } from '../../../assets/icons/chevron-down.svg';
import { requestTutorialRestart } from '../../tutorial/tutorial-storage';
import { notificationsEnabled } from '../../../config/feature-flags';

const TopBar = () => {
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const navigate = useNavigate();
   const useConditionalKeycloak = () => isKeycloakIDP ? useKeycloak() : null;

   const keycloakContext = useConditionalKeycloak();
   const keycloak = keycloakContext ? keycloakContext.keycloak : null;

   const [translate] = useTranslation(['common', 'invoice', 'account']);
   const { instance } = useMsal();
   const { data: userData } = useGetUserDataQuery();

   const ProfileIcon = React.forwardRef<
      HTMLDivElement,
      { onClick: (e: React.MouseEvent<HTMLDivElement>) => void }
   >(({ onClick }, ref) => (
      <div className='profile-container'>
         <Avatar className='profile-avatar' aria-hidden='true' />
         <div ref={ref}
            onClick={(e) => {
               e.preventDefault();
               onClick(e);
            }} className='username' >{userData?.firstName}</div>
         <ChevronDown className='profile-chevron' aria-hidden='true' />
      </div>
   ));

   const logoutFromApp = () => isAzureIDP ? azureLogout() : keycloakLogout();

   const azureLogout = () => {
      const logoutRequest = {
         postLogoutRedirectUri: '/',
         mainWindowRedirectUri: '/',
      };
      clearAccessTokenLocalStorage();
      instance.logoutRedirect(logoutRequest);
   }

   const keycloakLogout = () => {
      clearAccessTokenLocalStorage();
      keycloak && keycloak.logout();
   };

   return (
      <Navbar className='navbar-custom' variant='dark' data-tour='main-navigation'>
         <Navbar.Brand className='brand-link'>
            <Link to='/' aria-label={`${isKeycloakIDP ? 'Accountitz' : translate('app.appName')} ${getAppName()}`}>
               <AccountitzLogo className='brand-logo' />
            </Link>
         </Navbar.Brand>
         <Navbar.Collapse className='desktop-navigation' id="basic-navbar-nav" data-tour='desktop-navigation-anchor'>
            <Nav>
               <NavLink className={(navData) => (navData.isActive ? 'active' : 'link')} to='/'>
                  {translate('invoice:invoice.invoices')}
               </NavLink>
               <NavLink className={(navData) => (navData.isActive ? 'active' : 'link')} to='/bank-accounts'>
                  {translate('account:bankAccount.bankAccounts')}
               </NavLink>
            </Nav>
         </Navbar.Collapse>

         <div className='profile-navigation'>
            {userData && notificationsEnabled && <NotificationBell />}
            <Dropdown align='end'>
               <Dropdown.Toggle as={ProfileIcon} />
               <Dropdown.Menu>
                  <Dropdown.Item onClick={() => navigate('/profile')}>
                     {translate('profile.profile')}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => {
                     requestTutorialRestart();
                     navigate('/');
                  }}>
                     {translate('common:tutorial.restart')}
                  </Dropdown.Item>
                  <Dropdown.Item
                     onClick={logoutFromApp}
                  >
                     {translate('common:profile:logout')}
                  </Dropdown.Item>
               </Dropdown.Menu>
            </Dropdown>
            <span className='header-language-divider' aria-hidden='true' />
            <div className='header-language'>
               <LanguageSwitcher userData={userData} compact />
            </div>
            <button
               type='button'
               className='mobile-menu-toggle'
               data-tour='mobile-navigation-anchor'
               aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
               aria-expanded={mobileMenuOpen}
               onClick={() => setMobileMenuOpen(open => !open)}
            >
               {mobileMenuOpen ? <X size={28} /> : <List size={28} />}
            </button>
         </div>
         {mobileMenuOpen && (
            <div className='mobile-navigation'>
               <NavLink onClick={() => setMobileMenuOpen(false)} className={(navData) => (navData.isActive ? 'active' : 'link')} to='/'>
                  {translate('invoice:invoice.invoices')}
               </NavLink>
               <NavLink onClick={() => setMobileMenuOpen(false)} className={(navData) => (navData.isActive ? 'active' : 'link')} to='/bank-accounts'>
                  {translate('account:bankAccount.bankAccounts')}
               </NavLink>
               <button type='button' onClick={() => { setMobileMenuOpen(false); navigate('/profile'); }}>
                  {translate('profile.profile')}
               </button>
               <button type='button' onClick={logoutFromApp}>
                  {translate('common:profile:logout')}
               </button>
            </div>
         )}
      </Navbar>
   );
};

export default TopBar;
